import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
const css = `
.lead-btn {
    --accent: #22c55e;
    --accent-dark: #16a34a;
    
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    height: 48px;
    padding: 0 22px;
    border-radius: 14px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.01em;
    transition: transform 0.12s, box-shadow 0.18s, background 0.18s, border-color 0.18s;
    white-space: nowrap;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    flex-shrink: 0;
    position: relative;
    text-decoration: none;
    font-family: inherit;
    margin: 0;
    appearance: none;
    -webkit-appearance: none;
    line-height: 1;
}

.lead-btn:active {
    transform: scale(0.97);
}

.lead-btn:disabled,
.lead-btn[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    pointer-events: none;
}

/* PRIMARY */
.lead-btn--primary {
    background: var(--accent);
    color: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
}

.lead-btn--primary:hover {
    background: var(--accent-dark);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05);
}

/* SECONDARY */
.lead-btn--secondary {
    background: #fff;
    color: #1a1a1a;
    border: none;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
}

.lead-btn--secondary:hover {
    background: #fafafa;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05);
}

/* PULSE — только для primary */
.lead-btn--pulse::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: inherit;
    background: var(--accent);
    opacity: 0.4;
    z-index: -1;
    animation: leadPulse 2s ease-out infinite;
    pointer-events: none;
}

@keyframes leadPulse {
    0%   { transform: scale(1);    opacity: 0.4; }
    70%  { transform: scale(1.15); opacity: 0; }
    100% { transform: scale(1.15); opacity: 0; }
}

/* APPEAR — анимация появления */
@keyframes leadFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}

.lead-btn--appearing {
    animation: leadFadeIn 0.35s ease-out;
}

.lead-btn__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.lead-btn__icon svg {
    width: 30px;
    height: 30px;
    display: block;
}

.lead-btn__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    line-height: 1;
}

.lead-btn__label {
    font-weight: 600;
    line-height: 1.1;
}

.lead-btn__subtitle {
    font-size: 11px;
    font-weight: 400;
    opacity: 0.85;
    line-height: 1;
}

@media (max-width: 767px) {
    .lead-btn {
        width: 100%;
        justify-content: flex-start;
        height: 48px;
        padding: 0 14px;
    }
    .lead-btn__label {
        font-size: 13px;
    }
}
`;
function darken(hex, amount = 0.12) {
    const h = hex.replace('#', '');
    const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
    const r = Math.max(0, Math.floor(((num >> 16) & 0xff) * (1 - amount)));
    const g = Math.max(0, Math.floor(((num >> 8) & 0xff) * (1 - amount)));
    const b = Math.max(0, Math.floor((num & 0xff) * (1 - amount)));
    return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`;
}
function isExternalLink(href) {
    return /^https?:/i.test(href);
}
export function LeadButton({ label, subtitle, icon, variant = 'primary', accent, pulse = false, appearAfterMs, disabled, title, href, newTab, onClick, }) {
    const [visible, setVisible] = useState(appearAfterMs == null);
    useEffect(() => {
        if (appearAfterMs == null)
            return;
        const t = setTimeout(() => setVisible(true), appearAfterMs);
        return () => clearTimeout(t);
    }, [appearAfterMs]);
    if (!visible)
        return null;
    const className = [
        'lead-btn',
        `lead-btn--${variant}`,
        pulse && variant === 'primary' && 'lead-btn--pulse',
        appearAfterMs != null && 'lead-btn--appearing',
    ].filter(Boolean).join(' ');
    const style = accent && variant === 'primary'
        ? {
            ['--accent']: accent,
            ['--accent-dark']: darken(accent, 0.12),
        }
        : undefined;
    const content = (_jsxs(_Fragment, { children: [icon && _jsx("span", { className: "lead-btn__icon", children: icon }), _jsxs("span", { className: "lead-btn__content", children: [_jsx("span", { className: "lead-btn__label", children: label }), subtitle && _jsx("span", { className: "lead-btn__subtitle", children: subtitle })] })] }));
    const ariaLabel = title ?? label;
    const tooltip = title ?? label;
    // ─── ССЫЛКА ─────────────────────────────────────
    if (href) {
        const external = isExternalLink(href);
        // Новая вкладка по умолчанию — только для http/https
        const openInNewTab = newTab ?? external;
        return (_jsxs(_Fragment, { children: [_jsx("style", { children: css }), _jsx("a", { className: className, style: style, href: disabled ? undefined : href, target: "_blank", rel: openInNewTab ? 'noopener noreferrer' : undefined, onClick: () => { if (!disabled)
                        onClick?.(); }, title: tooltip, "aria-label": ariaLabel, "aria-disabled": disabled || undefined, role: "button", children: content })] }));
    }
    // ─── КНОПКА ─────────────────────────────────────
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: css }), _jsx("button", { type: "button", className: className, style: style, onClick: onClick, disabled: disabled, title: tooltip, "aria-label": ariaLabel, children: content })] }));
}
